/**
 * Iter 110 — Live-events WebSocket client.
 *
 * Connects to /api/ws/live-events, auto-reconnects with exponential
 * back-off on drop, and dispatches incoming events as a custom DOM
 * event (`ndaku-live-event`) so any component can subscribe with a
 * simple `window.addEventListener` — no React context plumbing.
 *
 * Event payload shape (matches backend `_process_live_match`):
 *   { type: "goal"|"kickoff"|"halftime"|"fulltime"|"red_card",
 *     fixture_id, home_team, away_team, league, score, status, minute,
 *     scorer_team_name?, scorer_team_id?, scorer_side?, ... }
 *
 * Usage:
 *   import { startLiveEventsSocket } from "./lib/liveEventsSocket";
 *   startLiveEventsSocket();   // idempotent
 *   window.addEventListener("ndaku-live-event", (e) => console.log(e.detail));
 */

const EVENT_NAME = "ndaku-live-event";
const STATE = {
  ws: null,
  reconnectAttempts: 0,
  reconnectTimer: null,
  starting: false,
  manuallyStopped: false,
};

const _resolveWsUrl = () => {
  if (typeof window === "undefined") return null;
  const backendUrl = window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL;
  // Same-origin host? Use ws[s]://<location.host>/api/ws/live-events
  const host = window.location.hostname;
  const sameOriginHosts = new Set(["ndakubet.com", "www.ndakubet.com"]);
  if (sameOriginHosts.has(host) || !backendUrl) {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${window.location.host}/api/ws/live-events`;
  }
  // Else convert https://host → wss://host
  try {
    const url = new URL(backendUrl);
    const proto = url.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${url.host}/api/ws/live-events`;
  } catch (_) {
    return null;
  }
};

const _scheduleReconnect = () => {
  if (STATE.manuallyStopped) return;
  STATE.reconnectAttempts += 1;
  // Exponential back-off with jitter, capped at 30s.
  const base = Math.min(30000, 1000 * 2 ** Math.min(STATE.reconnectAttempts, 5));
  const delay = base * (0.7 + Math.random() * 0.6);
  if (STATE.reconnectTimer) clearTimeout(STATE.reconnectTimer);
  STATE.reconnectTimer = setTimeout(() => _connect(), delay);
};

const _connect = () => {
  const url = _resolveWsUrl();
  if (!url) return;
  try {
    const ws = new WebSocket(url);
    STATE.ws = ws;

    ws.addEventListener("open", () => {
      STATE.reconnectAttempts = 0;
    });

    ws.addEventListener("message", (msg) => {
      let data;
      try {
        data = JSON.parse(msg.data);
      } catch (_) {
        return;
      }
      if (!data || typeof data !== "object") return;
      // Skip the hello envelope — it's a connection greeting, not a match event.
      if (data.type === "hello") return;
      try {
        window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: data }));
      } catch (_) {}
    });

    ws.addEventListener("close", () => {
      STATE.ws = null;
      _scheduleReconnect();
    });

    ws.addEventListener("error", () => {
      // `close` will fire next; just let the close handler reconnect.
      try { ws.close(); } catch (_) {}
    });
  } catch (_) {
    _scheduleReconnect();
  }
};

export const startLiveEventsSocket = () => {
  if (typeof window === "undefined") return;
  if (STATE.starting || STATE.ws) return;
  STATE.starting = true;
  STATE.manuallyStopped = false;
  _connect();
  STATE.starting = false;

  // Reconnect on tab regaining focus or network coming back.
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !STATE.ws) {
      STATE.reconnectAttempts = 0;
      _connect();
    }
  });
  window.addEventListener("online", () => {
    if (!STATE.ws) {
      STATE.reconnectAttempts = 0;
      _connect();
    }
  });
};

export const stopLiveEventsSocket = () => {
  STATE.manuallyStopped = true;
  if (STATE.reconnectTimer) {
    clearTimeout(STATE.reconnectTimer);
    STATE.reconnectTimer = null;
  }
  if (STATE.ws) {
    try { STATE.ws.close(); } catch (_) {}
    STATE.ws = null;
  }
};

export const LIVE_EVENT_NAME = EVENT_NAME;
